import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from '@/lib/caixaToast';
import { CardapioCaixaNotifications } from '@/components/CardapioCaixaNotifications';
import { SearchProduto, type SearchProdutoRef } from '@caixa/components/SearchProduto';
import { Carrinho } from '@caixa/components/Carrinho';
import { PaymentChoiceModal } from '@caixa/components/PaymentChoiceModal';
import { CashPaymentModal } from '@caixa/components/CashPaymentModal';
import { SplitPaymentModal } from '@caixa/components/SplitPaymentModal';
import { DesperdicioFromCartModal } from '@caixa/components/DesperdicioFromCartModal';
import { RelatorioCaixaModal } from '@caixa/components/RelatorioCaixaModal';
import { RetiradaEntradaModal } from '@caixa/components/RetiradaEntradaModal';
import { ComandaModal } from '@caixa/components/ComandaModal';
import { AlterarMesaModal } from '@caixa/components/AlterarMesaModal';
import { EnviarCozinhaConfirmModal } from '@caixa/components/EnviarCozinhaConfirmModal';
import { ComandasListModal, ComandaItemParaCarrinho } from '@caixa/components/ComandasListModal';
import { PromoConfiguravelModal } from '@caixa/components/PromoConfiguravelModal';
import { PromoCategoriaModal } from '@caixa/components/PromoCategoriaModal';
import { fetchProdutosCaixa, nfceEmitir, nfceStatus, vendasCriar, vendasValidarEstoque } from '@caixa/lib/api';
import {
  formatCpfCnpjMask,
  formatDocumentoConsumidorLabel,
  onlyDocumentDigits,
  validarDocumentoConsumidor,
} from '@caixa/lib/cpf';
import {
  cancelarItensCozinha,
  closeComanda,
  createComanda,
  updateComanda,
  alterarMesaComanda,
  GARCOM_FECHAR_COMANDA_STORAGE_KEY,
} from '@caixa/lib/comandasApi';
import type { ProdutoCaixa, VendaCriarPayload } from '@caixa/lib/api';
import type { ItemCarrinho, ValoresPagamento } from '@caixa/types';
import { totalItem, buildItensParaPagamento, totalSelecionadoPagamento, syncCartUnitSelection, unitSelectionFromMarcado, marcadoFromUnitSelection, normalizeItemObservacao, type CartUnitSelection } from '@caixa/types';
import { config } from '@caixa/config';
import { addCartItem, updateCartQuantity, removedComandaItemIds } from '@caixa/lib/cartQuantities';
import { unidadeVendaPermiteDecimal } from '@/lib/unidadeVenda';
import { getClientConfig, getCachedClientConfig } from '@caixa/lib/clientConfig';
import { getAtendimentoLabels, normalizeModoAtendimento } from '@/lib/atendimentoMode';
import { gerarCupomHtml, imprimirCupom } from '@/lib/cupom';
import {
  imprimirNfceDanfe,
  nfceProntoParaDanfe,
  NFC_E_QR_UNAVAILABLE_MSG,
} from '@caixa/lib/nfceDanfePrint';

const STORAGE_KEY = 'caixa-vendas';

function generateId(): string {
  return crypto.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeMesaEdicao(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function cozinhaItemKey(produtoId: number, nome: string, observacao?: string | null): string {
  return `${produtoId}\u001f${nome}\u001f${normalizeItemObservacao(observacao)}`;
}

function loadVendaLocal(valores: ValoresPagamento, items: ItemCarrinho[], total: number): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const vendas: Array<{ data: string; total: number; itens: ItemCarrinho[]; valores: ValoresPagamento }> = raw
      ? JSON.parse(raw)
      : [];
    vendas.unshift({
      data: new Date().toISOString(),
      total,
      itens: items,
      valores,
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vendas.slice(0, 200)));
  } catch {
    // ignore
  }
}

export type PrintConfirmData = {
  items: ItemCarrinho[];
  valores: ValoresPagamento;
  totalGeral: number;
};

type ComandaKdsBaselineItem = {
  cartItemId: string;
  itemId: number;
  quantidade: number;
  cozinhaPendente: boolean;
};

type FinalizacaoComanda = {
  comandaId: number;
  itens: ComandaKdsBaselineItem[];
};

const PAGAMENTO_VALIDACAO_ESTOQUE: ValoresPagamento = {
  pix: 0,
  dinheiro: 0,
  credito: 0,
  debito: 0,
};

function buildVendaCriarPayload(
  vendaItems: ItemCarrinho[],
  total: number,
  pagamento: ValoresPagamento,
): VendaCriarPayload {
  return {
    itens: vendaItems.map((i) => ({
      produto_id: i.cod,
      quantidade: i.quantidade,
      preco_unitario: i.precoUnitario,
      nome_produto: i.nome,
      observacao: normalizeItemObservacao(i.observacao) || undefined,
      ...(i.promocao_id != null && i.itens_escolhidos?.length
        ? { promocao_id: i.promocao_id, itens_escolhidos: i.itens_escolhidos }
        : {}),
    })),
    total,
    pagamento,
  };
}

export default function App() {
  const searchProdutoRef = useRef<SearchProdutoRef>(null);
  const [produtos, setProdutos] = useState<ProdutoCaixa[]>([]);
  const [produtosLoaded, setProdutosLoaded] = useState(false);
  const [items, setItems] = useState<ItemCarrinho[]>([]);
  const [desagrupar, setDesagrupar] = useState(false);
  const [unitSelection, setUnitSelection] = useState<CartUnitSelection>({});
  const [paymentChoiceOpen, setPaymentChoiceOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [totalParaPagamento, setTotalParaPagamento] = useState(0);
  const [printConfirm, setPrintConfirm] = useState<PrintConfirmData | null>(null);
  const [cupomCpfInput, setCupomCpfInput] = useState('');
  const [nfceLoading, setNfceLoading] = useState(false);
  const [saleProcessing, setSaleProcessing] = useState(false);
  /** Impede criar a venda / imprimir mais de uma vez no mesmo fluxo. */
  const saleFinalizeLockRef = useRef(false);
  const createdVendaIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (printConfirm) setCupomCpfInput('');
  }, [printConfirm]);
  const [desperdicioFromCartOpen, setDesperdicioFromCartOpen] = useState(false);
  const [relatorioOpen, setRelatorioOpen] = useState(false);
  const [retiradaEntradaOpen, setRetiradaEntradaOpen] = useState(false);
  const [relatorioRefreshTrigger, setRelatorioRefreshTrigger] = useState(0);
  const [comandaModalOpen, setComandaModalOpen] = useState(false);
  const [comandasListOpen, setComandasListOpen] = useState(false);
  const [comandasRefreshVersion, setComandasRefreshVersion] = useState(0);
  const bumpComandasList = useCallback(() => {
    setComandasRefreshVersion((v) => v + 1);
  }, []);
  const [comandaLoading, setComandaLoading] = useState(false);
  const [enviarCozinhaConfirmOpen, setEnviarCozinhaConfirmOpen] = useState(false);
  const [alterarMesaOpen, setAlterarMesaOpen] = useState(false);
  const [enviarCozinhaConfirmPurpose, setEnviarCozinhaConfirmPurpose] = useState<
    'salvar-edicao' | 'finalizar-venda'
  >('salvar-edicao');
  /** Evita reenviar à cozinha um pedido que já está no KDS. */
  const [kitchenAlreadySent, setKitchenAlreadySent] = useState(false);
  const [cartComandaNome, setCartComandaNome] = useState<string | null>(null);
  const [finalizacaoComanda, setFinalizacaoComanda] = useState<FinalizacaoComanda | null>(null);
  const [editingComandaId, setEditingComandaId] = useState<number | null>(null);
  const [editingComandaNome, setEditingComandaNome] = useState<string | null>(null);
  const [editingComandaOriginalNome, setEditingComandaOriginalNome] = useState<string | null>(null);
  /** IDs dos itens carregados ao abrir a edição — novos itens têm outro id. */
  const [editingComandaOriginalItemIds, setEditingComandaOriginalItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Quantidades originais por id do carrinho — detecta aumento ao re-adicionar o mesmo produto. */
  const [editingComandaOriginalQtys, setEditingComandaOriginalQtys] = useState<Map<string, number>>(
    () => new Map(),
  );
  /** Observações originais por id — detecta alteração de observação na edição. */
  const [editingComandaOriginalObs, setEditingComandaOriginalObs] = useState<Map<string, string>>(
    () => new Map(),
  );
  /** Itens da edicao que continuam pendentes no KDS no momento em que foram carregados. */
  const [editingComandaKdsPendingItemIds, setEditingComandaKdsPendingItemIds] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [pendingPromoProduct, setPendingPromoProduct] = useState<ProdutoCaixa | null>(null);
  const [pendingPromoCategoria, setPendingPromoCategoria] = useState<ProdutoCaixa | null>(null);
  const [nfceDisponivel, setNfceDisponivel] = useState(
    () => getCachedClientConfig().nfce_disponivel === true,
  );
  const [modoAtendimento, setModoAtendimento] = useState(() =>
    normalizeModoAtendimento(getCachedClientConfig().modo_atendimento),
  );
  const atendimentoLabels = useMemo(() => getAtendimentoLabels(modoAtendimento), [modoAtendimento]);

  useEffect(() => {
    void getClientConfig().then((c) => {
      setNfceDisponivel(c.nfce_disponivel === true);
      setModoAtendimento(normalizeModoAtendimento(c.modo_atendimento));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchProdutosCaixa()
      .then((data) => {
        if (!cancelled) setProdutos(data);
      })
      .finally(() => {
        if (!cancelled) setProdutosLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    searchProdutoRef.current?.focusSearch();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F1' || e.key === 'F2') e.preventDefault();
      if (
        paymentChoiceOpen ||
        splitOpen ||
        cashOpen ||
        desperdicioFromCartOpen ||
        printConfirm ||
        comandaModalOpen ||
        comandasListOpen ||
        pendingPromoProduct != null ||
        pendingPromoCategoria != null
      )
        return;
      if (e.key === 'F1') setRelatorioOpen(true);
      if (e.key === 'F2') setRetiradaEntradaOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    paymentChoiceOpen,
    splitOpen,
    cashOpen,
    desperdicioFromCartOpen,
    printConfirm,
    comandaModalOpen,
    comandasListOpen,
    pendingPromoProduct,
    pendingPromoCategoria,
  ]);

  /** Total só dos itens/unidades selecionados (pagamento / finalização). */
  const totalGeral = useMemo(
    () => totalSelecionadoPagamento(items, desagrupar, unitSelection),
    [items, desagrupar, unitSelection],
  );
  /** Soma de todas as linhas do carrinho (mesa, desperdício). */
  const totalCarrinhoCompleto = useMemo(
    () => items.reduce((s, i) => s + totalItem(i), 0),
    [items],
  );

  const podeFinalizarPagamento = useMemo(
    () => buildItensParaPagamento(items, desagrupar, unitSelection).length > 0,
    [items, desagrupar, unitSelection],
  );

  useEffect(() => {
    setUnitSelection((prev) => syncCartUnitSelection(items, prev));
  }, [items]);

  const handleDesagruparChange = useCallback((value: boolean) => {
    if (value) {
      setUnitSelection(unitSelectionFromMarcado(items));
      setDesagrupar(true);
      return;
    }
    setItems((prev) => marcadoFromUnitSelection(prev, unitSelection));
    setDesagrupar(false);
  }, [items, unitSelection]);

  const handleUnitMarcadoChange = useCallback((id: string, unitIndex: number, marcado: boolean) => {
    setUnitSelection((prev) => {
      const current = prev[id] ? [...prev[id]] : [];
      current[unitIndex] = marcado;
      return { ...prev, [id]: current };
    });
  }, []);

  const temProdutoNovoNaEdicaoComanda = useMemo(() => {
    if (editingComandaId == null) return false;
    return items.some((i) => !editingComandaOriginalItemIds.has(i.id));
  }, [editingComandaId, editingComandaOriginalItemIds, items]);

  const temQuantidadeAumentadaNaEdicao = useMemo(() => {
    if (editingComandaId == null) return false;
    return items.some((i) => {
      const orig = editingComandaOriginalQtys.get(i.id);
      if (orig == null) return false;
      return i.quantidade > orig + 0.0001;
    });
  }, [editingComandaId, editingComandaOriginalQtys, items]);

  const itensRemovidosPendentesNaEdicao = useMemo(() => {
    if (editingComandaId == null) return [];
    return removedComandaItemIds([...editingComandaKdsPendingItemIds.entries()].map(([cartItemId, itemId]) => ({ cartItemId, itemId })), items);
  }, [editingComandaId, editingComandaKdsPendingItemIds, items]);

  const temAlteracaoRelevanteKdsNaEdicao =
    temProdutoNovoNaEdicaoComanda ||
    temQuantidadeAumentadaNaEdicao ||
    itensRemovidosPendentesNaEdicao.length > 0;

  const itensNovosParaCozinhaNaFinalizacao = useMemo(() => {
    if (finalizacaoComanda == null) return [];
    const qtyOriginalByCartItemId = new Map<string, number>();
    for (const item of finalizacaoComanda.itens) {
      qtyOriginalByCartItemId.set(
        item.cartItemId,
        (qtyOriginalByCartItemId.get(item.cartItemId) ?? 0) + item.quantidade,
      );
    }
    return items.flatMap((item) => {
      const quantidadeOriginal = qtyOriginalByCartItemId.get(item.id);
      const quantidadeNova = quantidadeOriginal == null
        ? item.quantidade
        : Math.max(0, item.quantidade - quantidadeOriginal);
      if (quantidadeNova <= 0.0001) return [];

      const desconto = item.quantidade > 0
        ? Math.round(((item.desconto ?? 0) * quantidadeNova / item.quantidade) * 100) / 100
        : 0;
      return [{ ...item, quantidade: quantidadeNova, desconto }];
    });
  }, [finalizacaoComanda, items]);

  const itensRemovidosNaFinalizacao = useMemo(() => {
    if (finalizacaoComanda == null) return [];
    return removedComandaItemIds(finalizacaoComanda.itens, items);
  }, [finalizacaoComanda, items]);

  const itensRemovidosPendentesNaFinalizacao = useMemo(() => {
    if (finalizacaoComanda == null) return [];
    return removedComandaItemIds(finalizacaoComanda.itens, items, true);
  }, [finalizacaoComanda, items]);

  const temAlteracaoRelevanteKdsNaFinalizacao =
    itensNovosParaCozinhaNaFinalizacao.length > 0 ||
    itensRemovidosPendentesNaFinalizacao.length > 0;

  const temAlteracaoNosItensNaEdicaoComanda = useMemo(() => {
    if (editingComandaId == null) return false;
    const currentIds = new Set(items.map((i) => i.id));
    const temProdutoRemovido = [...editingComandaOriginalItemIds].some((id) => !currentIds.has(id));
    const temQtyAlterada = items.some((i) => {
      const orig = editingComandaOriginalQtys.get(i.id);
      if (orig == null) return false;
      return Math.abs(i.quantidade - orig) > 0.0001;
    });
    const temObsAlterada = items.some((i) => {
      if (!editingComandaOriginalItemIds.has(i.id)) return false;
      const orig = editingComandaOriginalObs.get(i.id) ?? '';
      return normalizeItemObservacao(i.observacao) !== orig;
    });
    return temProdutoNovoNaEdicaoComanda || temProdutoRemovido || temQtyAlterada || temObsAlterada;
  }, [
    editingComandaId,
    editingComandaOriginalItemIds,
    editingComandaOriginalQtys,
    editingComandaOriginalObs,
    items,
    temProdutoNovoNaEdicaoComanda,
  ]);

  const temMesaAlteradaNaEdicaoComanda = useMemo(() => {
    if (editingComandaId == null) return false;
    return normalizeMesaEdicao(editingComandaNome) !== normalizeMesaEdicao(editingComandaOriginalNome);
  }, [editingComandaId, editingComandaNome, editingComandaOriginalNome]);

  const temAlteracaoNaEdicaoComanda = useMemo(
    () => temAlteracaoNosItensNaEdicaoComanda || temMesaAlteradaNaEdicaoComanda,
    [temAlteracaoNosItensNaEdicaoComanda, temMesaAlteradaNaEdicaoComanda],
  );

  useEffect(() => {
    if (paymentChoiceOpen) setTotalParaPagamento(totalGeral);
  }, [paymentChoiceOpen, totalGeral]);

  const handleAdd = useCallback(
    (item: {
      cod: number;
      nome: string;
      quantidade: number;
      precoUnitario: number;
      permiteDecimal?: boolean;
      observacao?: string;
      promocao_id?: number;
      itens_escolhidos?: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>;
    }) => {
      const observacao = normalizeItemObservacao(item.observacao);
      const produto = produtos.find((p) => p.id === item.cod);
      const permiteDecimal = produto ? unidadeVendaPermiteDecimal(produto.unidade_venda) : item.permiteDecimal ?? false;
      const id = generateId();
      setItems((prev) => addCartItem(prev, { ...item, observacao, permiteDecimal }, id));
    },
    [produtos]
  );

  useEffect(() => {
    setItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const produto = produtos.find((p) => p.id === item.cod);
        if (!produto) return item;
        const permiteDecimal = unidadeVendaPermiteDecimal(produto.unidade_venda);
        if (item.permiteDecimal === permiteDecimal) return item;
        changed = true;
        return { ...item, permiteDecimal };
      });
      return changed ? next : prev;
    });
  }, [produtos, items]);

  const handleMarcadoChange = useCallback((id: string, marcado: boolean) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, marcado } : i)));
    setUnitSelection((prev) => {
      const flags = prev[id];
      if (!flags || flags.length === 0) return prev;
      return { ...prev, [id]: flags.map(() => marcado) };
    });
  }, []);

  const handleUpdateQty = useCallback((id: string, delta: number, groupIds?: string[]) => {
    setItems((prev) => updateCartQuantity(prev, id, delta, groupIds));
  }, []);

  const handleRemove = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const handleUpdatePreco = useCallback((id: string, preco: number, unitIndex?: number | null) => {
    if (unitIndex == null) {
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, precoUnitario: preco } : i))
      );
      return;
    }

    const nextId = generateId();
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0) return prev;
      const item = prev[idx];
      const slots = Math.max(1, Math.round(item.quantidade));
      if (item.permiteDecimal || slots <= 1 || unitIndex < 0 || unitIndex >= slots) {
        return prev.map((i) => (i.id === id ? { ...i, precoUnitario: preco } : i));
      }

      const descontoUnit = item.quantidade > 0
        ? Math.round(((item.desconto ?? 0) / item.quantidade) * 100) / 100
        : 0;
      const originalQty = slots - 1;
      const originalDesconto = Math.max(0, Math.round(((item.desconto ?? 0) - descontoUnit) * 100) / 100);
      const unidadeEditada: ItemCarrinho = {
        ...item,
        id: nextId,
        quantidade: 1,
        precoUnitario: preco,
        desconto: descontoUnit,
      };
      const originalAtualizado: ItemCarrinho = {
        ...item,
        quantidade: originalQty,
        desconto: originalDesconto,
      };
      return [
        ...prev.slice(0, idx),
        ...(originalQty > 0 ? [originalAtualizado] : []),
        unidadeEditada,
        ...prev.slice(idx + 1),
      ];
    });
    setUnitSelection((prev) => {
      const flags = prev[id];
      if (!flags) return prev;
      const editedMarked = flags[unitIndex] ?? true;
      const nextFlags = flags.filter((_, index) => index !== unitIndex);
      const next = { ...prev, [nextId]: [editedMarked] };
      if (nextFlags.length > 0) next[id] = nextFlags;
      else delete next[id];
      return next;
    });
  }, []);

  const handleUpdateObservacao = useCallback((id: string, observacao: string) => {
    const obs = normalizeItemObservacao(observacao);
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        if (!obs) {
          const { observacao: _removed, ...rest } = i;
          return rest;
        }
        return { ...i, observacao: obs };
      })
    );
  }, []);

  const handleConfirmPayment = useCallback(
    async (valores: ValoresPagamento, totalComDesconto?: number) => {
      const itensPagos = buildItensParaPagamento(items, desagrupar, unitSelection);
      if (itensPagos.length === 0) {
        const msg = 'Marque ao menos um item para finalizar.';
        toast.error(msg);
        throw new Error(msg);
      }
      const totalFinal = totalComDesconto ?? totalGeral;
      saleFinalizeLockRef.current = false;
      createdVendaIdRef.current = null;
      setSaleProcessing(false);
      setPaymentChoiceOpen(false);
      setSplitOpen(false);
      setCashOpen(false);
      setPrintConfirm({
        items: itensPagos.map((i) => ({ ...i })),
        valores,
        totalGeral: totalFinal,
      });
    },
    [items, totalGeral, desagrupar, unitSelection]
  );

  const cancelPrintConfirm = useCallback(() => {
    if (saleFinalizeLockRef.current || createdVendaIdRef.current != null || nfceLoading || saleProcessing)
      return;
    setPrintConfirm(null);
    searchProdutoRef.current?.focusSearch();
  }, [nfceLoading, saleProcessing]);

  const handleCancelarFinalizacaoComanda = useCallback(() => {
    if (comandaLoading || saleProcessing || nfceLoading) return;
    setItems([]);
    setDesagrupar(false);
    setUnitSelection({});
    setKitchenAlreadySent(false);
    setCartComandaNome(null);
    setFinalizacaoComanda(null);
    setPaymentChoiceOpen(false);
    setSplitOpen(false);
    setCashOpen(false);
    setPrintConfirm(null);
    setEnviarCozinhaConfirmOpen(false);
    setEnviarCozinhaConfirmPurpose('salvar-edicao');
    bumpComandasList();
    searchProdutoRef.current?.focusSearch();
  }, [bumpComandasList, comandaLoading, nfceLoading, saleProcessing]);

  useEffect(() => {
    if (!printConfirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelPrintConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [printConfirm, cancelPrintConfirm]);

  const finishAfterPrintChoice = useCallback(
    async (confirmed: PrintConfirmData) => {
      const finalizacaoAtual = finalizacaoComanda;
      loadVendaLocal(confirmed.valores, confirmed.items, confirmed.totalGeral);
      const paidQtyById = new Map<string, number>();
      for (const paid of confirmed.items) {
        paidQtyById.set(paid.id, (paidQtyById.get(paid.id) ?? 0) + paid.quantidade);
      }
      const nextItems = items.flatMap((i) => {
        const paidQty = paidQtyById.get(i.id);
        if (paidQty == null) return [{ ...i, marcado: true }];
        const remain = Math.round((i.quantidade - paidQty) * 1000) / 1000;
        if (remain <= 0.0001) return [];
        const descontoRemain =
          i.quantidade > 0
            ? Math.round((i.desconto * remain) / i.quantidade * 100) / 100
            : 0;
        return [{ ...i, quantidade: remain, desconto: descontoRemain, marcado: true }];
      });

      setItems(nextItems);
      setFinalizacaoComanda((current) => {
        if (current == null) return null;
        const itensRestantes = nextItems.map((item) => ({
          cartItemId: item.id,
          itemId: 0,
          quantidade: item.quantidade,
          cozinhaPendente: false,
        }));
        return itensRestantes.length > 0 ? { ...current, itens: itensRestantes } : null;
      });
      setUnitSelection({});
      setPrintConfirm(null);
      setSaleProcessing(false);

      // Fecha o financeiro sem apagar pedidos ainda pendentes no KDS.
      if (editingComandaId != null && nextItems.length === 0) {
        const comandaId = editingComandaId;
        setEditingComandaId(null);
        setEditingComandaNome(null);
        setEditingComandaOriginalNome(null);
        setEditingComandaOriginalItemIds(new Set());
        setEditingComandaOriginalQtys(new Map());
        setEditingComandaOriginalObs(new Map());
        setEditingComandaKdsPendingItemIds(new Map());
        try {
          await closeComanda(comandaId);
          bumpComandasList();
        } catch (e) {
          console.error(e);
        }
      }
      if (finalizacaoAtual != null && nextItems.length === 0) {
        try {
          await closeComanda(finalizacaoAtual.comandaId);
          bumpComandasList();
        } catch (e) {
          console.error(e);
        }
      }
      searchProdutoRef.current?.focusSearch();
    },
    [editingComandaId, finalizacaoComanda, items, bumpComandasList]
  );

  /** Cria a venda uma única vez (baixa estoque). */
  const ensureVendaCriada = useCallback(async (pending: PrintConfirmData): Promise<number> => {
    if (createdVendaIdRef.current != null) return createdVendaIdRef.current;
    const identificacao = (editingComandaId != null ? editingComandaNome : cartComandaNome)?.trim();
    const comandaIdOrigem = editingComandaId ?? finalizacaoComanda?.comandaId;
    const res = await vendasCriar({
      ...buildVendaCriarPayload(pending.items, pending.totalGeral, pending.valores),
      ...(comandaIdOrigem != null || identificacao ? {
        ...(comandaIdOrigem != null ? { comanda_id: comandaIdOrigem } : {}),
        atendimento_origem: { identificacao: identificacao ?? '', tipo: modoAtendimento },
      } : {}),
    });
    createdVendaIdRef.current = res.venda_id;
    return res.venda_id;
  }, [editingComandaId, editingComandaNome, cartComandaNome, finalizacaoComanda, modoAtendimento]);

  const claimSaleFinalize = useCallback(() => {
    if (saleFinalizeLockRef.current) return false;
    saleFinalizeLockRef.current = true;
    setSaleProcessing(true);
    return true;
  }, []);

  const releaseSaleFinalizeIfNotCreated = useCallback(() => {
    if (createdVendaIdRef.current == null) {
      saleFinalizeLockRef.current = false;
      setSaleProcessing(false);
    }
  }, []);

  const resolveConsumidorDocumento = useCallback(():
    | { ok: true; digits: string; kind: '' | 'cpf' | 'cnpj' }
    | { ok: false; message: string } => {
    const digits = onlyDocumentDigits(cupomCpfInput);
    if (digits.length === 0) return { ok: true, digits: '', kind: '' };
    const check = validarDocumentoConsumidor(digits);
    if (!check.ok) {
      return { ok: false, message: 'Informe um CPF ou CNPJ válido.' };
    }
    return { ok: true, digits, kind: check.kind };
  }, [cupomCpfInput]);

  const handleImprimirCupomNaoFiscal = useCallback(async () => {
    if (!printConfirm || !claimSaleFinalize()) return;
    const docCheck = resolveConsumidorDocumento();
    if (!docCheck.ok) {
      releaseSaleFinalizeIfNotCreated();
      toast.error(docCheck.message);
      return;
    }
    const pending = printConfirm;
    try {
      const venda_id = await ensureVendaCriada(pending);
      const itens = pending.items.map((i) => ({
        produto_id: i.cod,
        nome_produto: i.nome,
        quantidade: i.quantidade,
        preco_unitario: i.precoUnitario,
        subtotal: i.quantidade * i.precoUnitario - (i.desconto || 0),
        observacao: normalizeItemObservacao(i.observacao) || undefined,
      }));
      const subtotal_geral = itens.reduce((s, i) => s + i.quantidade * i.preco_unitario, 0);
      const total_geral = pending.totalGeral;
      const desconto = Math.max(0, subtotal_geral - total_geral);
      const cliente_identificacao = formatDocumentoConsumidorLabel(docCheck.digits, docCheck.kind);
      const venda = {
        venda_id,
        data_hora: new Date().toISOString(),
        vendedor: 'CAIXA',
        itens,
        total_pecas: pending.items.reduce((s, i) => s + i.quantidade, 0),
        subtotal_geral,
        total_geral,
        cliente_identificacao,
        ...(desconto > 0.005 ? { desconto } : {}),
        pagamento: pending.valores,
      };
      const html = gerarCupomHtml(venda, config.empresa);
      try {
        await imprimirCupom(html);
      } catch (_) {
        toast.error('Venda salva, mas não foi possível imprimir.');
      }
      toast.success('Venda finalizada com sucesso.');
      await finishAfterPrintChoice(pending);
    } catch (e) {
      releaseSaleFinalizeIfNotCreated();
      const msg = e instanceof Error ? e.message : 'Erro ao finalizar venda';
      toast.error(msg);
    }
  }, [
    printConfirm,
    claimSaleFinalize,
    releaseSaleFinalizeIfNotCreated,
    ensureVendaCriada,
    finishAfterPrintChoice,
    resolveConsumidorDocumento,
  ]);

  const handleEmitirNfce = useCallback(async () => {
    if (!printConfirm || nfceLoading || !claimSaleFinalize()) return;
    const docCheck = resolveConsumidorDocumento();
    if (!docCheck.ok) {
      releaseSaleFinalizeIfNotCreated();
      toast.error(docCheck.message);
      return;
    }
    const pending = printConfirm;
    setNfceLoading(true);
    const danfeOpts = {
      docDigits: docCheck.digits,
      docKind: docCheck.kind,
      items: pending.items,
      valores: pending.valores,
    };
    try {
      const venda_id = await ensureVendaCriada(pending);
      const emit = await nfceEmitir({
        venda_id,
        documento: docCheck.digits,
      });

      if (nfceProntoParaDanfe(emit)) {
        await imprimirNfceDanfe(emit, danfeOpts);
        toast.success('Venda finalizada com sucesso.');
        await finishAfterPrintChoice(pending);
        return;
      }

      toast.success(
        emit.authorized ? 'NFC-e autorizada. Gerando cupom...' : 'Nota enviada. Aguardando autorização...',
      );
      const ref = emit.ref;
      for (let attempt = 0; attempt < 10; attempt++) {
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
        const status = await nfceStatus(ref);
        if (nfceProntoParaDanfe(status)) {
          await imprimirNfceDanfe(status, danfeOpts);
          toast.success('Venda finalizada com sucesso.');
          await finishAfterPrintChoice(pending);
          return;
        }
      }
      toast.error(NFC_E_QR_UNAVAILABLE_MSG);
      toast.success('Venda finalizada com sucesso.');
      await finishAfterPrintChoice(pending);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Falha na emissão da NFC-e';
      toast.error(msg);
      console.error('[NFC-e]', e);
      if (createdVendaIdRef.current != null) {
        toast.success('Venda finalizada com sucesso.');
        await finishAfterPrintChoice(pending);
      } else {
        releaseSaleFinalizeIfNotCreated();
      }
    } finally {
      setNfceLoading(false);
    }
  }, [
    printConfirm,
    nfceLoading,
    claimSaleFinalize,
    releaseSaleFinalizeIfNotCreated,
    resolveConsumidorDocumento,
    ensureVendaCriada,
    finishAfterPrintChoice,
  ]);

  const handleNaoImprimir = useCallback(async () => {
    if (!printConfirm || !claimSaleFinalize()) return;
    const pending = printConfirm;
    try {
      await ensureVendaCriada(pending);
      toast.success('Venda finalizada com sucesso.');
      await finishAfterPrintChoice(pending);
    } catch (e) {
      releaseSaleFinalizeIfNotCreated();
      const msg = e instanceof Error ? e.message : 'Erro ao finalizar venda';
      toast.error(msg);
    }
  }, [
    printConfirm,
    claimSaleFinalize,
    releaseSaleFinalizeIfNotCreated,
    ensureVendaCriada,
    finishAfterPrintChoice,
  ]);

  const handleAbrirComandaConfirm = useCallback(
    async ({ nome, enviarCozinha, comandaId }: { nome: string; enviarCozinha: boolean; comandaId?: number }) => {
      if (items.length === 0) {
        toast.error(`Adicione itens ao carrinho antes de abrir a ${atendimentoLabels.singularLower}.`);
        return;
      }
      setComandaLoading(true);
      try {
        const res = await createComanda({
          nome: nome.trim(),
          comandaId,
          itens: items.map((i) => ({
            ...i,
            cod: i.cod,
            nome: i.nome,
            quantidade: i.quantidade,
            precoUnitario: i.precoUnitario,
            desconto: i.desconto ?? 0,
            observacao: normalizeItemObservacao(i.observacao) || undefined,
          })),
          total: totalCarrinhoCompleto,
          enviarCozinha,
        });
        setItems([]);
        setComandaModalOpen(false);
        if (res.action === 'added') {
          toast.success(
            enviarCozinha
              ? `Itens adicionados ${atendimentoLabels.destino}.`
              : `Itens adicionados ${atendimentoLabels.destino} (sem envio à cozinha).`,
          );
        } else {
          toast.success(
            enviarCozinha
              ? `${atendimentoLabels.singular} aberta.`
              : `${atendimentoLabels.singular} aberta (sem envio à cozinha).`,
          );
        }
        bumpComandasList();
        searchProdutoRef.current?.focusSearch();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : `Erro ao abrir ${atendimentoLabels.singularLower}.`);
      } finally {
        setComandaLoading(false);
      }
    },
    [items, totalCarrinhoCompleto, bumpComandasList, atendimentoLabels]
  );

  const handleFecharComanda = useCallback(
    (
      itens: ComandaItemParaCarrinho[],
      meta?: { jaEnviadoCozinha?: boolean; nome?: string; comandaId?: number },
    ) => {
      const novosItens: ItemCarrinho[] = [];
      const itensBase: ComandaKdsBaselineItem[] = [];
      for (const i of itens) {
        const observacao = normalizeItemObservacao(i.observacao);
        const cartItemId = i.entrada_id ?? generateId();
        novosItens.push({
          id: cartItemId,
          cod: i.produto_id,
          nome: i.nome_produto,
          quantidade: i.quantidade,
          precoUnitario: i.preco_unitario,
          desconto: i.desconto ?? 0,
          promocao_id: i.promocao_id ?? undefined,
          itens_escolhidos: i.itens_escolhidos,
          marcado: true,
          ...(observacao ? { observacao } : {}),
        });
        if (typeof i.id === 'number' && i.id > 0) {
          itensBase.push({
            cartItemId,
            itemId: i.id,
            quantidade: i.quantidade,
            cozinhaPendente: i.cozinha_pendente === true,
          });
        }
      }
      setKitchenAlreadySent(meta?.jaEnviadoCozinha === true);
      setCartComandaNome(meta?.nome?.trim() ? meta.nome.trim() : null);
      setFinalizacaoComanda(
        meta?.comandaId != null ? { comandaId: meta.comandaId, itens: itensBase } : null,
      );
      setItems(novosItens);
      setComandasListOpen(false);
      searchProdutoRef.current?.focusSearch();
    },
    []
  );

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(GARCOM_FECHAR_COMANDA_STORAGE_KEY);
      if (!raw) return;
      sessionStorage.removeItem(GARCOM_FECHAR_COMANDA_STORAGE_KEY);
      const parsed = JSON.parse(raw) as {
        itens?: Array<{
          produto_id: number;
          nome_produto: string;
          quantidade: number;
          preco_unitario: number;
          observacao?: string | null;
        }>;
        ja_enviado_cozinha?: boolean;
        nome?: string;
      };
      if (!Array.isArray(parsed.itens) || parsed.itens.length === 0) return;
      handleFecharComanda(parsed.itens, {
        jaEnviadoCozinha: parsed.ja_enviado_cozinha === true,
        nome: typeof parsed.nome === 'string' ? parsed.nome : undefined,
      });
    } catch {
      // ignore payload inválido
    }
  }, [handleFecharComanda]);

  const handleEditarComanda = useCallback(
    (comandaId: number, nome: string, itens: ComandaItemParaCarrinho[]) => {
      const novosItens: ItemCarrinho[] = itens.map((i) => {
        const observacao = normalizeItemObservacao(i.observacao);
        return {
          id: i.entrada_id ?? generateId(),
          cod: i.produto_id,
          nome: i.nome_produto,
          quantidade: i.quantidade,
          precoUnitario: i.preco_unitario,
          desconto: i.desconto ?? 0,
          promocao_id: i.promocao_id ?? undefined,
          itens_escolhidos: i.itens_escolhidos,
          marcado: true,
          ...(observacao ? { observacao } : {}),
        };
      });
      setEditingComandaId(comandaId);
      setEditingComandaNome(nome);
      setEditingComandaOriginalNome(nome);
      setEditingComandaOriginalItemIds(new Set(novosItens.map((i) => i.id)));
      setEditingComandaOriginalQtys(new Map(novosItens.map((i) => [i.id, i.quantidade])));
      setEditingComandaOriginalObs(
        new Map(novosItens.map((i) => [i.id, normalizeItemObservacao(i.observacao)])),
      );
      setEditingComandaKdsPendingItemIds(
        new Map(
          novosItens.flatMap((item, index) => {
            const origem = itens[index];
            return origem?.cozinha_pendente === true && typeof origem.id === 'number' && origem.id > 0
              ? [[item.id, origem.id] as const]
              : [];
          }),
        ),
      );
      setKitchenAlreadySent(false);
      setCartComandaNome(null);
      setFinalizacaoComanda(null);
      setItems(novosItens);
      setComandasListOpen(false);
      searchProdutoRef.current?.focusSearch();
    },
    []
  );

  const handlePromoConfiguravelConfirm = useCallback(
    (itens: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>) => {
      if (!pendingPromoProduct) return;
      handleAdd({
        cod: pendingPromoProduct.id,
        nome: pendingPromoProduct.nome,
        quantidade: 1,
        precoUnitario: pendingPromoProduct.preco_venda ?? 0,
        promocao_id: pendingPromoProduct.promocao_id ?? undefined,
        itens_escolhidos: itens,
      });
      setPendingPromoProduct(null);
      searchProdutoRef.current?.focusSearch();
    },
    [pendingPromoProduct, handleAdd]
  );

  const handlePromoCategoriaConfirm = useCallback(
    (itens: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>) => {
      if (!pendingPromoCategoria) return;
      handleAdd({
        cod: pendingPromoCategoria.id,
        nome: pendingPromoCategoria.nome,
        quantidade: 1,
        precoUnitario: pendingPromoCategoria.preco_venda ?? 0,
        promocao_id: pendingPromoCategoria.promocao_id ?? undefined,
        itens_escolhidos: itens,
      });
      setPendingPromoCategoria(null);
      searchProdutoRef.current?.focusSearch();
    },
    [pendingPromoCategoria, handleAdd]
  );

  const handleConfirmSalvarComandaEdicao = useCallback(
    async (enviarCozinha: boolean) => {
      if (!editingComandaId) return;
      const comandaIdAtual = editingComandaId;
      const mesaDestino = editingComandaNome ?? '';
      const deveSalvarItens = temAlteracaoNosItensNaEdicaoComanda;
      const deveAlterarMesa =
        temMesaAlteradaNaEdicaoComanda && normalizeMesaEdicao(mesaDestino) !== '';
      setComandaLoading(true);
      try {
        if (deveSalvarItens) {
          await updateComanda({
            id: comandaIdAtual,
            itens: items.map((i) => ({
              ...i,
              cod: i.cod,
              nome: i.nome,
              quantidade: i.quantidade,
              precoUnitario: i.precoUnitario,
              desconto: i.desconto ?? 0,
              observacao: normalizeItemObservacao(i.observacao) || undefined,
            })),
            total: totalCarrinhoCompleto,
            enviarCozinha,
          });
        }

        let mesaResult: { nome: string; action: string } | null = null;
        if (deveAlterarMesa) {
          mesaResult = await alterarMesaComanda({ id: comandaIdAtual, mesa: mesaDestino, identificacaoLivre: true });
        }
        setItems([]);
        setEditingComandaId(null);
        setEditingComandaNome(null);
        setEditingComandaOriginalNome(null);
        setEditingComandaOriginalItemIds(new Set());
        setEditingComandaOriginalQtys(new Map());
        setEditingComandaOriginalObs(new Map());
        setEditingComandaKdsPendingItemIds(new Map());
        setEnviarCozinhaConfirmOpen(false);
        if (mesaResult?.action === 'merged') {
          toast.success(`${atendimentoLabels.singular} salva e mesclada em ${mesaResult.nome}.`);
        } else if (mesaResult) {
          toast.success(`Identificação alterada para ${mesaResult.nome}.`);
        } else {
          toast.success(
            enviarCozinha
              ? `${atendimentoLabels.singular} salva.`
              : `${atendimentoLabels.singular} salva (sem envio à cozinha).`,
          );
        }
        bumpComandasList();
        searchProdutoRef.current?.focusSearch();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : `Erro ao salvar ${atendimentoLabels.singularLower}.`);
      } finally {
        setComandaLoading(false);
      }
    },
    [
      editingComandaId,
      editingComandaNome,
      items,
      totalCarrinhoCompleto,
      temAlteracaoNosItensNaEdicaoComanda,
      temMesaAlteradaNaEdicaoComanda,
      bumpComandasList,
      atendimentoLabels,
    ]
  );

  const handleSalvarComandaEdicao = useCallback(() => {
    if (!editingComandaId) return;
    if (!temAlteracaoNaEdicaoComanda) return;
    if (temAlteracaoRelevanteKdsNaEdicao) {
      setEnviarCozinhaConfirmPurpose('salvar-edicao');
      setEnviarCozinhaConfirmOpen(true);
      return;
    }
    void handleConfirmSalvarComandaEdicao(false);
  }, [
    editingComandaId,
    temAlteracaoNaEdicaoComanda,
    temAlteracaoRelevanteKdsNaEdicao,
    handleConfirmSalvarComandaEdicao,
  ]);

  /** Sai da edição sem salvar, pagar ou excluir a mesa no servidor. */
  const handleCancelarComandaEdicao = useCallback(() => {
    if (editingComandaId == null) return;
    setEnviarCozinhaConfirmOpen(false);
    setAlterarMesaOpen(false);
    setEditingComandaId(null);
    setEditingComandaNome(null);
    setEditingComandaOriginalNome(null);
    setEditingComandaOriginalItemIds(new Set());
    setEditingComandaOriginalQtys(new Map());
    setEditingComandaOriginalObs(new Map());
    setEditingComandaKdsPendingItemIds(new Map());
    setKitchenAlreadySent(false);
    setCartComandaNome(null);
    setFinalizacaoComanda(null);
    setItems([]);
    setUnitSelection({});
    searchProdutoRef.current?.focusSearch();
  }, [editingComandaId]);

  const persistirItensEdicaoAntesPagamento = useCallback(
    async (enviarCozinha: boolean) => {
      if (editingComandaId == null || !temAlteracaoNosItensNaEdicaoComanda) return;
      await updateComanda({
        id: editingComandaId,
        itens: items.map((item) => ({
          ...item,
          cod: item.cod,
          nome: item.nome,
          quantidade: item.quantidade,
          precoUnitario: item.precoUnitario,
          desconto: item.desconto ?? 0,
          observacao: normalizeItemObservacao(item.observacao) || undefined,
        })),
        total: totalCarrinhoCompleto,
        enviarCozinha,
      });
      bumpComandasList();
    },
    [
      editingComandaId,
      temAlteracaoNosItensNaEdicaoComanda,
      items,
      totalCarrinhoCompleto,
      bumpComandasList,
    ],
  );

  const handleAlterarMesaConfirm = useCallback(
    async (mesa: string) => {
      if (editingComandaId == null) return;
      const mesaFormatada = mesa.trim();
      const voltouParaMesaOriginal =
        normalizeMesaEdicao(mesaFormatada) === normalizeMesaEdicao(editingComandaOriginalNome);
      setEditingComandaNome(mesaFormatada);
      setAlterarMesaOpen(false);
      toast.success(
        voltouParaMesaOriginal
          ? `${atendimentoLabels.singular} voltou para ${mesaFormatada}.`
          : `${atendimentoLabels.singular} selecionada: ${mesaFormatada}. Clique em Salvar para confirmar.`,
      );
      searchProdutoRef.current?.focusSearch();
    },
    [editingComandaId, editingComandaOriginalNome, atendimentoLabels],
  );

  useEffect(() => {
    if (items.length > 0 || editingComandaId != null) return;
    setKitchenAlreadySent(false);
    setCartComandaNome(null);
    setFinalizacaoComanda(null);
  }, [items.length, editingComandaId]);

  const handleFinalizarClick = useCallback(async () => {
    const itensVenda = buildItensParaPagamento(items, desagrupar, unitSelection);
    if (itensVenda.length === 0) {
      toast.error('Marque ao menos um item para finalizar.');
      return;
    }

    setComandaLoading(true);
    try {
      await vendasValidarEstoque(
        buildVendaCriarPayload(itensVenda, totalGeral, PAGAMENTO_VALIDACAO_ESTOQUE),
      );
      const edicaoSemAlteracaoRelevanteKds =
        editingComandaId != null && !temAlteracaoRelevanteKdsNaEdicao;
      const finalizacaoSemAlteracaoRelevanteKds =
        finalizacaoComanda != null && !temAlteracaoRelevanteKdsNaFinalizacao;
      if (edicaoSemAlteracaoRelevanteKds || finalizacaoSemAlteracaoRelevanteKds) {
        if (edicaoSemAlteracaoRelevanteKds) {
          await persistirItensEdicaoAntesPagamento(false);
        }
        if (finalizacaoComanda != null && itensRemovidosNaFinalizacao.length > 0) {
          await cancelarItensCozinha(finalizacaoComanda.comandaId, itensRemovidosNaFinalizacao, false);
          bumpComandasList();
        }
        setPaymentChoiceOpen(true);
        return;
      }
      setEnviarCozinhaConfirmPurpose('finalizar-venda');
      setEnviarCozinhaConfirmOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao preparar a finalizacao.');
    } finally {
      setComandaLoading(false);
    }
  }, [
    items,
    desagrupar,
    unitSelection,
    totalGeral,
    editingComandaId,
    finalizacaoComanda,
    temAlteracaoRelevanteKdsNaEdicao,
    temAlteracaoRelevanteKdsNaFinalizacao,
    itensRemovidosNaFinalizacao,
    persistirItensEdicaoAntesPagamento,
    bumpComandasList,
  ]);

  const handleConfirmFinalizarVenda = useCallback(
    async (enviarCozinha: boolean) => {
      if (items.length === 0) return;
      setComandaLoading(true);
      try {
        const itensParaCozinha = finalizacaoComanda != null
          ? itensNovosParaCozinhaNaFinalizacao
          : kitchenAlreadySent
            ? []
            : items;

        if (editingComandaId != null) {
          await persistirItensEdicaoAntesPagamento(enviarCozinha);
        } else {
          if (finalizacaoComanda != null && itensRemovidosNaFinalizacao.length > 0) {
            await cancelarItensCozinha(
              finalizacaoComanda.comandaId,
              itensRemovidosNaFinalizacao,
              enviarCozinha,
            );
            bumpComandasList();
          }

          if (enviarCozinha && itensParaCozinha.length > 0) {
            const nome = cartComandaNome?.trim() || 'Balcão';
            const res = await createComanda({
              nome,
              itens: itensParaCozinha.map((i) => ({
                ...i,
                cod: i.cod,
                nome: i.nome,
                quantidade: i.quantidade,
                precoUnitario: i.precoUnitario,
                desconto: i.desconto ?? 0,
                observacao: normalizeItemObservacao(i.observacao) || undefined,
              })),
              total: itensParaCozinha.reduce((sum, item) => sum + totalItem(item), 0),
              enviarCozinha: true,
            });
            setKitchenAlreadySent(true);
            const cartItemIdByKey = new Map(
              itensParaCozinha.map((item) => [
                cozinhaItemKey(item.cod, item.nome, item.observacao),
                item.id,
              ]),
            );
            let itensCriados: Awaited<ReturnType<typeof closeComanda>>['itens'] = [];
            try {
              const fechado = await closeComanda(res.comanda_id);
              itensCriados = fechado.itens;
            } catch {
              // Pedido ja foi para a cozinha; o Caixa segue para o pagamento.
            }
            if (finalizacaoComanda != null) {
              setFinalizacaoComanda((current) => {
                if (current == null) return current;
                const novosItensBase = itensCriados.flatMap((item) => {
                  const cartItemId = cartItemIdByKey.get(
                    cozinhaItemKey(item.produto_id, item.nome_produto, item.observacao),
                  );
                  return cartItemId == null
                    ? []
                    : [{
                      cartItemId,
                      itemId: item.id,
                      quantidade: item.quantidade,
                      cozinhaPendente: item.cozinha_pendente === true,
                    }];
                });
                if (novosItensBase.length === 0) {
                  return {
                    ...current,
                    itens: [
                      ...current.itens,
                      ...itensParaCozinha.map((item) => ({
                        cartItemId: item.id,
                        itemId: 0,
                        quantidade: item.quantidade,
                        cozinhaPendente: false,
                      })),
                    ],
                  };
                }
                return { ...current, itens: [...current.itens, ...novosItensBase] };
              });
            }
            bumpComandasList();
          }
        }
        setEnviarCozinhaConfirmOpen(false);
        setPaymentChoiceOpen(true);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Erro ao enviar para a cozinha.');
      } finally {
        setComandaLoading(false);
      }
    },
    [
      items,
      kitchenAlreadySent,
      cartComandaNome,
      editingComandaId,
      finalizacaoComanda,
      itensNovosParaCozinhaNaFinalizacao,
      itensRemovidosNaFinalizacao,
      persistirItensEdicaoAntesPagamento,
      bumpComandasList,
    ],
  );

  return (
    <div className="flex flex-col bg-[#141414] text-zinc-100 max-lg:overflow-x-hidden max-lg:overflow-y-visible lg:h-full lg:min-h-0 lg:overflow-hidden">
      <div className="container mx-auto flex max-w-7xl flex-col p-4 max-lg:flex-none lg:h-full lg:min-h-0 lg:flex-1">

        <div className="grid grid-cols-1 content-start gap-4 max-lg:overflow-visible lg:flex-1 lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:content-stretch lg:items-stretch lg:overflow-hidden">
          <aside className="flex min-w-0 shrink-0 flex-col gap-4 max-lg:overflow-visible lg:h-full lg:max-w-[380px] lg:min-h-0 lg:overflow-hidden">
            <SearchProduto
              ref={searchProdutoRef}
              produtos={produtos}
              produtosLoaded={produtosLoaded}
              onAdd={handleAdd}
              onSelectPromoConfiguravel={(p) => setPendingPromoProduct(p)}
              onSelectPromoCategoria={(p) => setPendingPromoCategoria(p)}
            />
            {/* Ações de mesa / venda: abaixo do card, acima de Relatório e Retirada/Entrada */}
            <div className="flex w-full shrink-0 flex-col gap-4">
            {editingComandaId ? (
              temAlteracaoNaEdicaoComanda ? (
                <div className="grid w-full grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={handleCancelarComandaEdicao}
                    disabled={comandaLoading}
                    className="min-w-0 py-3 rounded-md bg-[#333333] hover:bg-[#3d3d3d] border border-zinc-600 text-zinc-200 font-medium text-sm disabled:pointer-events-none disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSalvarComandaEdicao}
                    disabled={comandaLoading}
                    className="min-h-[44px] rounded-md border border-zinc-300/90 bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {comandaLoading ? 'Salvando…' : 'Salvar'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleCancelarComandaEdicao}
                  disabled={comandaLoading}
                  className="w-full min-h-[44px] shrink-0 rounded-md bg-[#333333] hover:bg-[#3d3d3d] border border-zinc-600 px-4 py-3 text-sm font-medium text-white disabled:pointer-events-none disabled:opacity-50"
                >
                  Cancelar
                </button>
              )
            ) : items.length === 0 ? (
              <button
                type="button"
                onClick={() => setComandasListOpen(true)}
                className="w-full min-h-[44px] shrink-0 rounded-md border border-zinc-300/90 bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200"
              >
                Ver {atendimentoLabels.pluralLower}
              </button>
            ) : (
              <div className="grid w-full grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={finalizacaoComanda ? handleCancelarFinalizacaoComanda : () => setComandaModalOpen(true)}
                  disabled={finalizacaoComanda != null && (comandaLoading || saleProcessing || nfceLoading)}
                  className={
                    finalizacaoComanda
                      ? "min-w-0 py-3 rounded-md bg-[#333333] hover:bg-[#3d3d3d] border border-zinc-600 text-zinc-200 font-medium text-sm disabled:pointer-events-none disabled:opacity-50"
                      : "min-h-[44px] rounded-md border border-zinc-300/90 bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200"
                  }
                >
                  {finalizacaoComanda ? 'Cancelar' : atendimentoLabels.abrir}
                </button>
                <button
                  type="button"
                  onClick={() => void handleFinalizarClick()}
                  disabled={!podeFinalizarPagamento || comandaLoading}
                  className="min-h-[44px] rounded-md border border-zinc-300/90 bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200 disabled:pointer-events-none disabled:opacity-50"
                >
                  Finalizar
                </button>
              </div>
            )}
            <div className="w-full shrink-0 rounded-lg border border-zinc-700 bg-[#242424] p-3 shadow-sm">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRelatorioOpen(true)}
                  className="min-w-0 py-3 rounded-md bg-[#333333] hover:bg-[#3d3d3d] border border-zinc-600 text-zinc-200 font-medium text-sm"
                >
                  Relatório (F1)
                </button>
                <button
                  type="button"
                  onClick={() => setRetiradaEntradaOpen(true)}
                  className="min-w-0 py-3 rounded-md bg-[#333333] hover:bg-[#3d3d3d] border border-zinc-600 text-zinc-200 font-medium text-sm"
                >
                  Retirada/Entrada (F2)
                </button>
              </div>
            </div>
            </div>
          </aside>
          <main className="flex flex-col max-lg:min-h-[280px] max-lg:flex-none lg:h-full lg:min-h-0 lg:flex-1">
            {editingComandaId && editingComandaNome && (
              <div className="mb-2 flex shrink-0 items-center justify-between gap-3 px-3 py-2 rounded-md border border-white/70 bg-white/15 text-sm text-white shadow-sm backdrop-blur-sm">
                <span className="min-w-0 truncate">
                  {atendimentoLabels.editando}: <span className="font-semibold">{editingComandaNome}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setAlterarMesaOpen(true)}
                  disabled={comandaLoading}
                  className="shrink-0 rounded-md border border-zinc-300/90 bg-white px-3 py-1.5 text-xs font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200 disabled:pointer-events-none disabled:opacity-50"
                >
                  Alterar {atendimentoLabels.singular}
                </button>
              </div>
            )}
            <Carrinho
              items={items}
              desagrupar={desagrupar}
              unitSelection={unitSelection}
              onDesagruparChange={handleDesagruparChange}
              onUpdateQty={handleUpdateQty}
              onRemove={handleRemove}
              onUpdatePreco={handleUpdatePreco}
              onUpdateObservacao={handleUpdateObservacao}
              onMarcadoChange={handleMarcadoChange}
              onUnitMarcadoChange={handleUnitMarcadoChange}
            />
          </main>
        </div>
      </div>

      <PaymentChoiceModal
        open={paymentChoiceOpen}
        total={totalGeral}
        onConfirm={handleConfirmPayment}
        onClose={() => {
          setPaymentChoiceOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
        onOpenSplit={(totalComDesconto) => {
          setTotalParaPagamento(totalComDesconto);
          setPaymentChoiceOpen(false);
          setSplitOpen(true);
        }}
        onOpenCash={(totalComDesconto) => {
          setTotalParaPagamento(totalComDesconto);
          setPaymentChoiceOpen(false);
          setCashOpen(true);
        }}
        onOpenDesperdicios={() => {
          setPaymentChoiceOpen(false);
          setDesperdicioFromCartOpen(true);
        }}
      />

      <DesperdicioFromCartModal
        open={desperdicioFromCartOpen}
        items={items}
        total={totalCarrinhoCompleto}
        onClose={() => {
          setDesperdicioFromCartOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
        onSuccess={() => {
          setItems([]);
          toast.success('Desperdício registrado!');
          searchProdutoRef.current?.focusSearch();
        }}
      />

      <SplitPaymentModal
        open={splitOpen}
        total={totalParaPagamento}
        onConfirm={handleConfirmPayment}
        onCancel={() => {
          setSplitOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
      />

      <CashPaymentModal
        open={cashOpen}
        total={totalParaPagamento}
        onConfirm={handleConfirmPayment}
        onCancel={() => {
          setCashOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
      />

      <RelatorioCaixaModal
        open={relatorioOpen}
        onClose={() => setRelatorioOpen(false)}
        refreshTrigger={relatorioRefreshTrigger}
      />

      <RetiradaEntradaModal
        open={retiradaEntradaOpen}
        onClose={() => setRetiradaEntradaOpen(false)}
        onSuccess={() => setRelatorioRefreshTrigger((t) => t + 1)}
      />

      <ComandaModal
        open={comandaModalOpen}
        modoAtendimento={modoAtendimento}
        onClose={() => {
          setComandaModalOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
        onConfirm={handleAbrirComandaConfirm}
        loading={comandaLoading}
      />

      <AlterarMesaModal
        open={alterarMesaOpen && editingComandaId != null}
        modoAtendimento={modoAtendimento}
        mesaAtual={editingComandaNome ?? ''}
        comandaId={editingComandaId ?? 0}
        loading={comandaLoading}
        onClose={() => setAlterarMesaOpen(false)}
        onConfirm={handleAlterarMesaConfirm}
      />

      <EnviarCozinhaConfirmModal
        open={enviarCozinhaConfirmOpen}
        loading={comandaLoading}
        onNao={() => {
          if (enviarCozinhaConfirmPurpose === 'finalizar-venda') {
            void handleConfirmFinalizarVenda(false);
          } else {
            void handleConfirmSalvarComandaEdicao(false);
          }
        }}
        onSim={() => {
          if (enviarCozinhaConfirmPurpose === 'finalizar-venda') {
            void handleConfirmFinalizarVenda(true);
          } else {
            void handleConfirmSalvarComandaEdicao(true);
          }
        }}
      />

      <ComandasListModal
        open={comandasListOpen}
        modoAtendimento={modoAtendimento}
        refreshVersion={comandasRefreshVersion}
        onClose={() => {
          setComandasListOpen(false);
          searchProdutoRef.current?.focusSearch();
        }}
        onFecharComanda={handleFecharComanda}
        onEditarComanda={handleEditarComanda}
      />

      <PromoConfiguravelModal
        open={pendingPromoProduct != null}
        produto={pendingPromoProduct}
        onClose={() => setPendingPromoProduct(null)}
        onConfirm={handlePromoConfiguravelConfirm}
      />

      <PromoCategoriaModal
        open={pendingPromoCategoria != null}
        produto={pendingPromoCategoria}
        onClose={() => setPendingPromoCategoria(null)}
        onConfirm={handlePromoCategoriaConfirm}
      />

      {printConfirm && (
        <div
          className="caixa-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="print-confirm-title"
        >
          <div className="caixa-modal caixa-modal--md">
            <h2 id="print-confirm-title" className="caixa-modal-title">
              Deseja imprimir a nota fiscal?
            </h2>
            <div className="caixa-field">
              <label htmlFor="cupom-cpf">CPF / CNPJ do consumidor</label>
              <input
                id="cupom-cpf"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="CPF / CNPJ"
                value={cupomCpfInput}
                onChange={(e) => setCupomCpfInput(formatCpfCnpjMask(e.target.value))}
                className="caixa-input caixa-input--333"
              />
            </div>
            <div className="caixa-modal-footer" style={{ flexWrap: "wrap" }}>
              {nfceDisponivel ? (
                <button
                  type="button"
                  disabled={nfceLoading || saleProcessing}
                  onClick={() => void handleEmitirNfce()}
                  className="caixa-btn-primary"
                >
                  {nfceLoading ? 'Emitindo…' : 'Emitir NFC-e'}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={nfceLoading || saleProcessing}
                  onClick={() => void handleImprimirCupomNaoFiscal()}
                  className="caixa-btn-primary"
                >
                  Cupom Não Fiscal
                </button>
              )}
              <button
                type="button"
                disabled={nfceLoading || saleProcessing}
                onClick={() => void handleNaoImprimir()}
                className="caixa-btn-danger"
              >
                Não emitir
              </button>
            </div>
          </div>
        </div>
      )}
      <CardapioCaixaNotifications />
    </div>
  );
}
